import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AlertOctagon, AlertCircle, Clock, Send, TrendingUp, TrendingDown, Minus, GitBranch } from "lucide-react";
import { suppliers } from "@/data/fixtures";
import SourceChip from "@/components/SourceChip";
import ApprovalModal from "@/components/ApprovalModal";
import useHashHighlight from "@/hooks/useHashHighlight";

const statusStyle = {
  lapsed: {
    label: "Lapsed",
    icon: AlertOctagon,
    color: "var(--mos-red)",
    bg: "var(--mos-red-bg)",
    border: "var(--mos-red-border)",
  },
  lapsing: {
    label: "Lapsing",
    icon: Clock,
    color: "var(--mos-amber)",
    bg: "var(--mos-amber-bg)",
    border: "var(--mos-amber-border)",
  },
};

const trendIcon = { up: TrendingUp, down: TrendingDown, flat: Minus };
const trendColor = { up: "var(--mos-green)", down: "var(--mos-red)", flat: "#94A3B8" };

export default function Suppliers() {
  const navigate = useNavigate();
  const [approveTarget, setApproveTarget] = useState(null);
  const [decomposed, setDecomposed] = useState(null);
  useHashHighlight();

  // Cross-module link targets · SP-02 traceability across modules.
  const crossLinks = {
    s1: {
      to: "/quality#d4",
      label: "See related PPAP defect",
      testId: "xlink-s1-to-ppap",
    },
    s2: {
      to: "/risk#r1",
      label: "See downstream commitment exposure",
      testId: "xlink-s2-to-risk",
    },
  };

  const onApproveSend = () => {
    toast.success(
      `Supplier request queued · ${approveTarget.supplier} · pending R. Diaz dispatch`,
      { duration: 4200 }
    );
    setApproveTarget(null);
  };

  return (
    <div data-testid="page-suppliers" className="space-y-6">
      {/* Persistent banner · SP-05 */}
      <div
        className="rounded-md border px-4 py-2.5 text-[12.5px]"
        style={{
          background: "#F1F5F9",
          borderColor: "var(--mos-border)",
          color: "var(--mos-text-muted)",
        }}
        data-testid="suppliers-banner"
      >
        <span className="font-medium text-slate-700">Note · </span>
        {suppliers.banner}
      </div>

      {/* Header */}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-2">
            Living Supplier File · Watchlist
          </div>
          <h1 className="text-[30px] leading-tight font-semibold text-slate-900 tracking-tight">
            {suppliers.summary.lapsed} lapsed · {suppliers.summary.lapsingIn30 + suppliers.summary.lapsingIn90} lapsing in 90 days
          </h1>
          <p className="mt-2 text-[13.5px] text-slate-600">
            {suppliers.summary.netChange}. Every drafted request is held for human approval before dispatch.
          </p>
        </div>
      </div>

      {/* Two-column layout · watchlist + supplier ranking */}
      <div className="grid grid-cols-3 gap-6">
        {/* Watchlist */}
        <div className="col-span-2 space-y-3.5" data-testid="supplier-watchlist">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">
            Watchlist · lapsed first
          </div>
          {suppliers.watchlist.map((w) => {
            const s = statusStyle[w.status];
            const Icon = s.icon;
            return (
              <div
                key={w.id}
                className="mos-surface rounded-md p-5"
                data-testid={`watchlist-item-${w.id}`}
                data-record-id={w.id}
              >
                <div className="flex items-start gap-4">
                  <span
                    className="h-8 w-8 rounded-full inline-flex items-center justify-center flex-none"
                    style={{ background: s.bg }}
                  >
                    <Icon size={14} style={{ color: s.color }} />
                  </span>

                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span
                        className="text-[10.5px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded border"
                        style={{ color: s.color, background: s.bg, borderColor: s.border }}
                      >
                        {s.label}
                      </span>
                      <span className="text-[15px] font-semibold text-slate-900">
                        {w.supplier}
                      </span>
                      <span className="text-[13px] text-slate-500">·</span>
                      <span className="text-[14px] text-slate-700">{w.document}</span>
                    </div>

                    <div className="mt-2 text-[13px] text-slate-700">
                      <span className="text-slate-500">{w.clockClass}</span>{" "}
                      · <span className="mos-mono text-slate-900">{w.dateLabel}</span>
                    </div>

                    <div className="mt-3 flex items-center gap-4 flex-wrap text-[12.5px]">
                      <div>
                        <span className="text-slate-500 mr-1.5">Affected parts</span>
                        <span className="text-slate-800">
                          {w.affectedParts.join(", ")}
                        </span>
                      </div>
                      {w.openOrders.length > 0 && (
                        <>
                          <span className="h-3 w-px bg-slate-200" />
                          <div>
                            <span className="text-slate-500 mr-1.5">Open orders</span>
                            <span className="mos-mono text-slate-900">
                              {w.openOrders.join(" · ")}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <SourceChip system={w.source.system} record={w.source.record} />
                      <span
                        className="mos-mono text-[11px] px-2 py-0.5 rounded"
                        style={{
                          background: "var(--mos-amber-bg)",
                          color: "var(--mos-amber)",
                        }}
                      >
                        {w.draftStatus}
                      </span>
                    </div>

                    <div
                      className="mt-4 text-[13px] text-slate-700 leading-relaxed rounded p-3 border"
                      style={{
                        background: "var(--mos-surface-2)",
                        borderColor: "var(--mos-border)",
                      }}
                    >
                      {w.draftBody}
                    </div>

                    {crossLinks[w.id] && (
                      <button
                        type="button"
                        onClick={() => navigate(crossLinks[w.id].to)}
                        className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-[color:var(--mos-accent)] hover:underline font-medium"
                        data-testid={crossLinks[w.id].testId}
                      >
                        <GitBranch size={12} />
                        {crossLinks[w.id].label} →
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setApproveTarget(w)}
                    className="mos-btn mos-btn-primary flex-none"
                    data-testid={`btn-approve-supplier-${w.id}`}
                  >
                    <Send size={13} />
                    Approve and send
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Supplier ranking */}
        <aside className="col-span-1 space-y-3">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">
            Documentation health
          </div>
          <div className="mos-surface rounded-md p-4 space-y-3" data-testid="supplier-ranking">
            {suppliers.ranking.map((r) => {
              const TrendIcon = trendIcon[r.trend];
              return (
                <div
                  key={r.supplier}
                  className="border-b last:border-b-0 pb-3 last:pb-0"
                  style={{ borderColor: "var(--mos-border)" }}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="text-[13.5px] font-medium text-slate-900">
                      {r.supplier}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setDecomposed(decomposed === r.supplier ? null : r.supplier)
                      }
                      className="text-[11.5px] text-[color:var(--mos-accent)] hover:underline"
                      data-testid={`btn-decompose-supplier-${r.supplier.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {decomposed === r.supplier ? "hide" : "decompose"}
                    </button>
                  </div>
                  <div className="mt-1.5 flex items-center gap-3">
                    <div className="mos-mono text-[15px] font-semibold text-slate-900">
                      {r.healthScore}
                    </div>
                    <TrendIcon size={13} style={{ color: trendColor[r.trend] }} />
                    <div
                      className="h-1.5 rounded-full overflow-hidden flex-1"
                      style={{ background: "#E5E7EB" }}
                    >
                      <div
                        style={{
                          width: `${r.healthScore}%`,
                          background: "var(--mos-accent)",
                          height: "100%",
                        }}
                      />
                    </div>
                  </div>
                  {decomposed === r.supplier && (
                    <div className="mt-2 text-[11.5px] text-slate-600 leading-relaxed">
                      Score is a weighted composite: certificate freshness (30%), R&R validity
                      (25%), PPAP element aging (25%), revision-drift signals (20%). See the
                      Watchlist for the triggering items.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      <ApprovalModal
        open={!!approveTarget}
        onCancel={() => setApproveTarget(null)}
        onApprove={onApproveSend}
        title="Send supplier request"
        rationale="Drafts are generated from watchlist facts only. Approval queues this request for R. Diaz to dispatch and marks the supplier response for tracking."
        approver="R. Diaz"
        action={
          approveTarget
            ? `Send ${approveTarget.document.toLowerCase()} request to ${approveTarget.supplier}`
            : ""
        }
        systemsAffected={["Supplier file", "Mailbox · outbound"]}
        auditNote="Dispatch, receipt, and verification will each write to the audit log."
        confirmLabel="Approve and queue"
      />
    </div>
  );
}
